import { useQuery } from '@tanstack/react-query';
import { apiGet, ApiError } from '@/api/client';

const SLUG = /^[a-z][a-z0-9-]{1,30}$/;
const LOOPBACK = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

/** La entidad dueña de un host (dominio propio) y el origen del API en el host canónico, para el salto de sesión. */
export interface HostEntity { slug: string; canonicalOrigin: string }

const byHost = new Map<string, Promise<HostEntity | null>>();

/**
 * Qué entidad tiene este host como dominio propio (`suite.ansv.gov.co`), según `GET /public/entities/by-host/:host`
 * (registrado y verificado por un superadministrador). `null` en el host canónico de la suite y en los que no son de
 * nadie. Con caché en memoria por host: la portada y el formulario de ingreso lo consultan sin repetir la petición.
 */
export function entityFromHost(host: string = window.location.hostname): Promise<HostEntity | null> {
  const key = host.toLowerCase();
  if (LOOPBACK.has(key) || !key.includes('.')) return Promise.resolve(null);
  let p = byHost.get(key);
  if (!p) {
    p = apiGet<HostEntity>(`/public/entities/by-host/${encodeURIComponent(key)}`).catch((e: unknown) => {
      // Sin dominio registrado es un 404 normal; otra falla no se guarda, para reintentar después.
      if (e instanceof ApiError && e.status === 404) return null;
      byHost.delete(key);
      return null;
    });
    byHost.set(key, p);
  }
  return p;
}

/**
 * Qué entidad es, antes de que haya sesión (la portada muestra solo sus apps). Siempre explícita en la URL, nunca
 * recordada: así el superadministrador llega a la portada sin marca con solo abrir `/ingreso`. En orden:
 * 1. El dominio: si el host es el dominio propio de una entidad (`entityFromHost`), es ella y la URL no lleva el slug.
 * 2. `?entidad=<slug>`: lo añade la suite al salir y al mandar a ingresar por sesión vencida, y las apps al pedir el
 *    ingreso; también dentro de `volver`.
 * 3. `?state=<slug>`: el cierre de sesión OIDC devuelve el `state` que mandó la app al `post_logout_redirect_uri`
 *    registrado (la portada), y las apps mandan ahí su entidad (GUIA §3.1, «Salir»).
 */
export function entityHint(search: URLSearchParams, fromHost: string | null = null): string | null {
  if (fromHost) return fromHost;
  const volver = search.get('volver') ?? '/';
  const enVolver = new URL(volver, window.location.origin).searchParams.get('entidad');
  const s = search.get('entidad') ?? enVolver ?? search.get('state');
  return s && SLUG.test(s) ? s : null;
}

/** `entityHint` con el dominio resuelto: `slug` vale cuando `listo`; mientras se consulta el host, nada se muestra. */
export function useEntityHint(search: URLSearchParams): { slug: string | null; listo: boolean; hostEntity: HostEntity | null } {
  const host = window.location.hostname;
  const q = useQuery({ queryKey: ['entity-by-host', host], queryFn: () => entityFromHost(host), staleTime: Infinity, retry: false });
  const listo = q.isFetched;
  return { slug: listo ? entityHint(search, q.data?.slug ?? null) : null, listo, hostEntity: q.data ?? null };
}
